// Box-vs-brush collision against a Source map's brushes, the way the engine does it
// (Quake/Source CM_ClipBoxToBrush): every brush is a convex set of planes; the player box is
// handled by pushing each plane out by the box's extent along its normal. Bevel sides are kept,
// they stop the box snagging on edges. Static brushes are found through a 2D grid built by the worker.
//
// Brush planes are stored in their solid's local space. Each solid has a current position
// (`offsets`), a yaw (spinning platforms) and an enabled flag, so map entities can move, rotate and
// switch brushes without rebuilding anything. Solids that move sideways or rotate are "dynamic":
// they're kept out of the grid and tested on every query instead.
//
// Rotated solids: the box is turned into the solid's frame and its extents grow to the rotated box's
// bounds (like Source's transformed box traces, slightly generous at odd angles).
// No allocations per trace.

import { DIST_EPSILON } from '../constants';
import type { Vec3 } from '../vec';
import { buildBrushGrid, type CollisionData } from './brushData';
import {
  CONTENTS_LADDER,
  CONTENTS_SOLID,
  MASK_WATER,
  type CollisionWorld,
  type SolidEntity,
  type TraceResult,
} from './types';

export class BspWorld implements CollisionWorld {
  /** One entity object per solid (index 0 = world), reported as the trace's `entity`. */
  readonly solids: SolidEntity[];
  /** Current position of each solid (3 per solid). */
  readonly offsets: Float64Array;
  /** Current yaw (radians) of each solid about its origin. */
  readonly yaw: Float64Array;
  /** 1 if the solid currently collides / triggers. */
  readonly enabled: Uint8Array;
  private readonly cosYaw: Float64Array;
  private readonly sinYaw: Float64Array;
  /** Solids also tilted about their X or Y axis (rotating doors): full rotation matrix in `rot`. */
  private readonly tilted: Uint8Array;
  /** Local -> world rotation, 9 per solid, row-major (only used when tilted). */
  private readonly rot: Float64Array;
  private readonly planes: Float64Array;
  private readonly brushes: Int32Array;
  private readonly bounds: Float64Array;
  private readonly stamp: Uint32Array;
  private visit = 0;
  private grid: CollisionData['grid'];
  /** Brushes of dynamic solids, tested on every query. */
  private dynamicBrushes = new Int32Array(0);
  /** First brush and brush count of each solid (brushes of a solid are contiguous). */
  private readonly solidFirst: Int32Array;
  private readonly solidCount: Int32Array;
  readonly hasLadders: boolean;
  readonly hasWater: boolean;

  // Per-brush clip results (written by clipBrush).
  private enterFrac = 0;
  private enterSide = -1;
  private clipStartOut = false;
  private clipGetOut = false;

  constructor(readonly data: CollisionData) {
    this.planes = data.planes;
    this.brushes = data.brushes;
    this.bounds = data.bounds;
    this.grid = data.grid;
    const ns = data.solids.length;
    this.stamp = new Uint32Array(Math.max(data.brushes.length / 4, ns));
    this.offsets = new Float64Array(ns * 3);
    this.yaw = new Float64Array(ns);
    this.cosYaw = new Float64Array(ns).fill(1);
    this.sinYaw = new Float64Array(ns);
    this.tilted = new Uint8Array(ns);
    this.rot = new Float64Array(ns * 9);
    this.enabled = new Uint8Array(ns);
    this.solidFirst = new Int32Array(ns).fill(-1);
    this.solidCount = new Int32Array(ns);
    this.solids = data.solids.map((s, i) => {
      this.offsets.set(s.origin, i * 3);
      this.enabled[i] = s.enabled === false ? 0 : 1;
      return { contents: CONTENTS_SOLID, solidIndex: i, classname: s.classname };
    });
    const nb = data.brushes.length / 4;
    let ladders = false;
    let water = false;
    for (let b = 0; b < nb; b++) {
      if (data.brushes[b * 4 + 2] & CONTENTS_LADDER) ladders = true;
      if (data.brushes[b * 4 + 2] & MASK_WATER) water = true;
    }
    this.hasLadders = ladders;
    this.hasWater = water;
    for (let b = 0; b < nb; b++) {
      const s = data.brushes[b * 4 + 3];
      if (this.solidFirst[s] < 0) this.solidFirst[s] = b;
      this.solidCount[s]++;
    }
  }

  get brushCount() {
    return this.brushes.length / 4;
  }

  /** Brush range of a solid, for callers that need to inspect its brushes. */
  solidBrushes(solid: number): [first: number, count: number] {
    return [this.solidFirst[solid], this.solidCount[solid]];
  }

  /** Set a solid's yaw (radians). */
  setYaw(solid: number, yaw: number) {
    this.yaw[solid] = yaw;
    this.cosYaw[solid] = Math.cos(yaw);
    this.sinYaw[solid] = Math.sin(yaw);
    this.tilted[solid] = 0;
  }

  /**
   * Set a solid's orientation: yaw, then a tilt about its own X (axis 0) or Y (axis 1) axis
   * (rotation = Rz(yaw) * Raxis(tilt)). A zero tilt is the plain yaw path.
   */
  setRotation(solid: number, yaw: number, axis: number, tilt: number) {
    this.setYaw(solid, yaw);
    if (tilt === 0) return;
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    const ct = Math.cos(tilt);
    const st = Math.sin(tilt);
    const m = this.rot;
    const o = solid * 9;
    if (axis === 0) {
      // Rz * Rx
      m[o] = c;
      m[o + 1] = -sn * ct;
      m[o + 2] = sn * st;
      m[o + 3] = sn;
      m[o + 4] = c * ct;
      m[o + 5] = -c * st;
      m[o + 6] = 0;
      m[o + 7] = st;
      m[o + 8] = ct;
    } else {
      // Rz * Ry
      m[o] = c * ct;
      m[o + 1] = -sn;
      m[o + 2] = c * st;
      m[o + 3] = sn * ct;
      m[o + 4] = c;
      m[o + 5] = sn * st;
      m[o + 6] = -st;
      m[o + 7] = 0;
      m[o + 8] = ct;
    }
    this.tilted[solid] = 1;
  }

  /** Mark solids that move sideways or rotate: they leave the grid and are tested on every query. */
  setDynamic(solids: readonly number[]) {
    const dyn = new Uint8Array(this.data.solids.length);
    for (const s of solids) dyn[s] = 1;
    const list: number[] = [];
    for (const s of solids)
      for (let b = this.solidFirst[s], e = b + this.solidCount[s]; b >= 0 && b < e; b++) list.push(b);
    this.dynamicBrushes = new Int32Array(list);
    this.grid = buildBrushGrid(
      this.brushes,
      this.bounds,
      this.data.solids,
      this.grid.cellSize,
      (b) => !dyn[this.brushes[b * 4 + 3]],
    );
  }

  /** Visit every brush whose grid cells overlap [x0,x1]x[y0,y1], plus all dynamic brushes, once each. */
  private query(x0: number, y0: number, x1: number, y1: number, fn: (b: number) => boolean | void): void {
    if (++this.visit === 0xffffffff) {
      this.stamp.fill(0);
      this.visit = 1;
    }
    const id = this.visit;
    const stamp = this.stamp;
    const g = this.grid;
    const cx0 = Math.max(0, Math.floor((x0 - g.originX) / g.cellSize));
    const cy0 = Math.max(0, Math.floor((y0 - g.originY) / g.cellSize));
    const cx1 = Math.min(g.nx - 1, Math.floor((x1 - g.originX) / g.cellSize));
    const cy1 = Math.min(g.ny - 1, Math.floor((y1 - g.originY) / g.cellSize));
    const items = g.items;
    const cellStart = g.cellStart;
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const c = cy * g.nx + cx;
        for (let k = cellStart[c], e = cellStart[c + 1]; k < e; k++) {
          const b = items[k];
          if (stamp[b] === id) continue;
          stamp[b] = id;
          if (fn(b)) return;
        }
      }
    }
    const dyn = this.dynamicBrushes;
    for (let k = 0; k < dyn.length; k++) if (fn(dyn[k])) return;
  }

  // Query state shared with the callbacks (kept on the instance so nothing is allocated).
  private tStart: Vec3 = [0, 0, 0];
  private tEnd: Vec3 = [0, 0, 0];
  private ext: Vec3 = [0, 0, 0];
  private sweepMin: Vec3 = [0, 0, 0];
  private sweepMax: Vec3 = [0, 0, 0];
  private mask = 0;
  private bestFrac = 1;
  private bestBrush = -1;
  private bestSide = -1;
  private bestCos = 1;
  private bestSin = 0;
  private bestSolid = -1;
  private startSolid = false;
  private allSolid = false;
  // Brush-local query values written by toLocal().
  private lsx = 0;
  private lsy = 0;
  private lsz = 0;
  private lfx = 0;
  private lfy = 0;
  private lfz = 0;
  private lex = 0;
  private ley = 0;
  private lez = 0;

  /** Skip test: brush disabled, filtered by mask, or its (world) bounds miss the query box. */
  private rejects(b: number, strict: boolean): boolean {
    const brushes = this.brushes;
    const solid = brushes[b * 4 + 3];
    if (!this.enabled[solid] || (brushes[b * 4 + 2] & this.mask) === 0) return true;
    const off = this.offsets;
    const bd = this.bounds;
    if (this.tilted[solid]) return this.rejectsTilted(b, solid, strict);
    const c = this.cosYaw[solid];
    const s = this.sinYaw[solid];
    let x0 = bd[b * 6];
    let y0 = bd[b * 6 + 1];
    let x1 = bd[b * 6 + 3];
    let y1 = bd[b * 6 + 4];
    if (s !== 0 || c !== 1) {
      // World bounds of the rotated local box.
      const cx = (x0 + x1) * 0.5;
      const cy = (y0 + y1) * 0.5;
      const hx = (x1 - x0) * 0.5;
      const hy = (y1 - y0) * 0.5;
      const wx = c * cx - s * cy;
      const wy = s * cx + c * cy;
      const ex = Math.abs(c) * hx + Math.abs(s) * hy;
      const ey = Math.abs(s) * hx + Math.abs(c) * hy;
      x0 = wx - ex;
      x1 = wx + ex;
      y0 = wy - ey;
      y1 = wy + ey;
    }
    const ox = off[solid * 3];
    const oy = off[solid * 3 + 1];
    const oz = off[solid * 3 + 2];
    const sMin = this.sweepMin;
    const sMax = this.sweepMax;
    if (strict)
      return (
        x0 + ox >= sMax[0] ||
        x1 + ox <= sMin[0] ||
        y0 + oy >= sMax[1] ||
        y1 + oy <= sMin[1] ||
        bd[b * 6 + 2] + oz >= sMax[2] ||
        bd[b * 6 + 5] + oz <= sMin[2]
      );
    return (
      x0 + ox > sMax[0] ||
      x1 + ox < sMin[0] ||
      y0 + oy > sMax[1] ||
      y1 + oy < sMin[1] ||
      bd[b * 6 + 2] + oz > sMax[2] ||
      bd[b * 6 + 5] + oz < sMin[2]
    );
  }

  private rejectsTilted(b: number, solid: number, strict: boolean): boolean {
    const bd = this.bounds;
    const m = this.rot;
    const o = solid * 9;
    const sMin = this.sweepMin;
    const sMax = this.sweepMax;
    for (let i = 0; i < 3; i++) {
      // World bounds of the rotated local box along axis i.
      let c = this.offsets[solid * 3 + i];
      let e = 0;
      for (let j = 0; j < 3; j++) {
        const rij = m[o + i * 3 + j];
        c += rij * (bd[b * 6 + j] + bd[b * 6 + 3 + j]) * 0.5;
        e += Math.abs(rij) * (bd[b * 6 + 3 + j] - bd[b * 6 + j]) * 0.5;
      }
      if (strict ? c - e >= sMax[i] || c + e <= sMin[i] : c - e > sMax[i] || c + e < sMin[i]) return true;
    }
    return false;
  }

  /** Box centre start/end and extents in the solid's local frame. */
  private toLocal(solid: number) {
    const off = this.offsets;
    const ox = off[solid * 3];
    const oy = off[solid * 3 + 1];
    const oz = off[solid * 3 + 2];
    const s = this.tStart;
    const e = this.tEnd;
    const c = this.cosYaw[solid];
    const sn = this.sinYaw[solid];
    const ax = s[0] - ox;
    const ay = s[1] - oy;
    const bx = e[0] - ox;
    const by = e[1] - oy;
    this.lsz = s[2] - oz;
    this.lfz = e[2] - oz;
    this.lez = this.ext[2];
    if (this.tilted[solid]) {
      // local = R^T (p - origin); extents grow to the rotated box's bounds.
      const m = this.rot;
      const o = solid * 9;
      const az = this.lsz;
      const bz = this.lfz;
      const ex = this.ext;
      this.lsx = m[o] * ax + m[o + 3] * ay + m[o + 6] * az;
      this.lsy = m[o + 1] * ax + m[o + 4] * ay + m[o + 7] * az;
      this.lsz = m[o + 2] * ax + m[o + 5] * ay + m[o + 8] * az;
      this.lfx = m[o] * bx + m[o + 3] * by + m[o + 6] * bz;
      this.lfy = m[o + 1] * bx + m[o + 4] * by + m[o + 7] * bz;
      this.lfz = m[o + 2] * bx + m[o + 5] * by + m[o + 8] * bz;
      this.lex = Math.abs(m[o]) * ex[0] + Math.abs(m[o + 3]) * ex[1] + Math.abs(m[o + 6]) * ex[2];
      this.ley = Math.abs(m[o + 1]) * ex[0] + Math.abs(m[o + 4]) * ex[1] + Math.abs(m[o + 7]) * ex[2];
      this.lez = Math.abs(m[o + 2]) * ex[0] + Math.abs(m[o + 5]) * ex[1] + Math.abs(m[o + 8]) * ex[2];
      return;
    }
    if (sn === 0 && c === 1) {
      this.lsx = ax;
      this.lsy = ay;
      this.lfx = bx;
      this.lfy = by;
      this.lex = this.ext[0];
      this.ley = this.ext[1];
      return;
    }
    // Rotate by -yaw.
    this.lsx = c * ax + sn * ay;
    this.lsy = -sn * ax + c * ay;
    this.lfx = c * bx + sn * by;
    this.lfy = -sn * bx + c * by;
    const ex = this.ext[0];
    const ey = this.ext[1];
    this.lex = Math.abs(c) * ex + Math.abs(sn) * ey;
    this.ley = Math.abs(sn) * ex + Math.abs(c) * ey;
  }

  private readonly traceBrush = (b: number): boolean | void => {
    if (this.rejects(b, false)) return;
    const solid = this.brushes[b * 4 + 3];
    this.toLocal(solid);
    this.clipBrush(b);
    if (!this.clipStartOut) {
      this.startSolid = true;
      if (!this.clipGetOut) {
        this.allSolid = true;
        this.bestFrac = 0;
        this.bestBrush = b;
        this.bestSide = -1;
      }
      return;
    }
    if (this.enterSide >= 0 && this.enterFrac < this.bestFrac) {
      this.bestFrac = Math.max(0, this.enterFrac);
      this.bestBrush = b;
      this.bestSide = this.enterSide;
      this.bestCos = this.cosYaw[solid];
      this.bestSin = this.sinYaw[solid];
      this.bestSolid = solid;
    }
  };

  /** CM_ClipBoxToBrush on the local values from toLocal(). */
  private clipBrush(b: number) {
    const p = this.planes;
    const first = this.brushes[b * 4];
    const count = this.brushes[b * 4 + 1];
    const ex = this.lex;
    const ey = this.ley;
    const ez = this.lez;
    const sx = this.lsx;
    const sy = this.lsy;
    const sz = this.lsz;
    const fx = this.lfx;
    const fy = this.lfy;
    const fz = this.lfz;
    let enterFrac = -1;
    let leaveFrac = 1;
    let enterSide = -1;
    let startOut = false;
    let getOut = false;
    for (let i = first, end = first + count; i < end; i++) {
      const nx = p[i * 4];
      const ny = p[i * 4 + 1];
      const nz = p[i * 4 + 2];
      // Push the plane out by the box extents.
      const dist = p[i * 4 + 3] + Math.abs(nx) * ex + Math.abs(ny) * ey + Math.abs(nz) * ez;
      const d1 = sx * nx + sy * ny + sz * nz - dist;
      const d2 = fx * nx + fy * ny + fz * nz - dist;
      if (d2 > 0) getOut = true; // endpoint is not in solid
      if (d1 > 0) startOut = true;
      // Completely in front of this face: no intersection with the brush.
      if (d1 > 0 && d2 >= d1) {
        this.clipStartOut = true;
        this.clipGetOut = true;
        this.enterSide = -1;
        return;
      }
      if (d1 <= 0 && d2 <= 0) continue;
      if (d1 > d2) {
        // Entering
        const f = (d1 - DIST_EPSILON) / (d1 - d2);
        if (f > enterFrac) {
          enterFrac = f;
          enterSide = i;
        }
      } else {
        // Leaving
        const f = (d1 + DIST_EPSILON) / (d1 - d2);
        if (f < leaveFrac) leaveFrac = f;
      }
    }
    this.clipStartOut = startOut;
    this.clipGetOut = getOut;
    this.enterFrac = enterFrac;
    this.enterSide = startOut && enterFrac < leaveFrac && enterFrac > -1 ? enterSide : -1;
  }

  trace(
    start: Readonly<Vec3>,
    end: Readonly<Vec3>,
    mins: Readonly<Vec3>,
    maxs: Readonly<Vec3>,
    mask: number,
    out: TraceResult,
  ): TraceResult {
    for (let k = 0; k < 3; k++) {
      const c = (mins[k] + maxs[k]) * 0.5;
      this.ext[k] = (maxs[k] - mins[k]) * 0.5;
      this.tStart[k] = start[k] + c;
      this.tEnd[k] = end[k] + c;
      this.sweepMin[k] = Math.min(start[k], end[k]) + mins[k] - 1;
      this.sweepMax[k] = Math.max(start[k], end[k]) + maxs[k] + 1;
    }
    this.mask = mask;
    this.bestFrac = 1;
    this.bestBrush = -1;
    this.bestSide = -1;
    this.bestSolid = -1;
    this.startSolid = false;
    this.allSolid = false;
    this.query(this.sweepMin[0], this.sweepMin[1], this.sweepMax[0], this.sweepMax[1], this.traceBrush);

    out.startSolid = this.startSolid;
    out.allSolid = this.allSolid;
    const f = this.bestFrac;
    out.fraction = f;
    if (f >= 1) {
      out.endPos[0] = end[0];
      out.endPos[1] = end[1];
      out.endPos[2] = end[2];
    } else {
      out.endPos[0] = start[0] + (end[0] - start[0]) * f;
      out.endPos[1] = start[1] + (end[1] - start[1]) * f;
      out.endPos[2] = start[2] + (end[2] - start[2]) * f;
    }
    const side = this.bestSide;
    if (side >= 0) {
      const nx = this.planes[side * 4];
      const ny = this.planes[side * 4 + 1];
      const bs = this.bestSolid;
      if (bs >= 0 && this.tilted[bs]) {
        const nz = this.planes[side * 4 + 2];
        const m = this.rot;
        const o = bs * 9;
        out.planeNormal[0] = m[o] * nx + m[o + 1] * ny + m[o + 2] * nz;
        out.planeNormal[1] = m[o + 3] * nx + m[o + 4] * ny + m[o + 5] * nz;
        out.planeNormal[2] = m[o + 6] * nx + m[o + 7] * ny + m[o + 8] * nz;
      } else {
        out.planeNormal[0] = this.bestCos * nx - this.bestSin * ny;
        out.planeNormal[1] = this.bestSin * nx + this.bestCos * ny;
        out.planeNormal[2] = this.planes[side * 4 + 2];
      }
    } else out.planeNormal[0] = out.planeNormal[1] = out.planeNormal[2] = 0;
    out.entity = this.bestBrush >= 0 ? this.solids[this.brushes[this.bestBrush * 4 + 3]] : null;
    out.contents = this.bestBrush >= 0 ? this.brushes[this.bestBrush * 4 + 2] : 0;
    return out;
  }

  /** Is the box (centre values from toLocal) inside brush b? Touching counts as inside. */
  private boxInBrush(b: number): boolean {
    const p = this.planes;
    const brushes = this.brushes;
    const ex = this.lex;
    const ey = this.ley;
    const ez = this.lez;
    for (let i = brushes[b * 4], end = i + brushes[b * 4 + 1]; i < end; i++) {
      const nx = p[i * 4];
      const ny = p[i * 4 + 1];
      const nz = p[i * 4 + 2];
      const dist = p[i * 4 + 3] + Math.abs(nx) * ex + Math.abs(ny) * ey + Math.abs(nz) * ez;
      if (this.lsx * nx + this.lsy * ny + this.lsz * nz - dist > 0) return false;
    }
    return true;
  }

  private found = false;
  private readonly testBrush = (b: number): boolean => {
    if (this.rejects(b, true)) return false;
    this.toLocal(this.brushes[b * 4 + 3]);
    if (!this.boxInBrush(b)) return false;
    this.found = true;
    return true;
  };

  private setupBox(origin: Readonly<Vec3>, mins: Readonly<Vec3>, maxs: Readonly<Vec3>, mask: number) {
    for (let k = 0; k < 3; k++) {
      this.ext[k] = (maxs[k] - mins[k]) * 0.5;
      this.tStart[k] = this.tEnd[k] = origin[k] + (mins[k] + maxs[k]) * 0.5;
      this.sweepMin[k] = origin[k] + mins[k];
      this.sweepMax[k] = origin[k] + maxs[k];
    }
    this.mask = mask;
  }

  testPosition(origin: Readonly<Vec3>, mins: Readonly<Vec3>, maxs: Readonly<Vec3>, mask: number): boolean {
    this.setupBox(origin, mins, maxs, mask);
    this.found = false;
    this.query(this.sweepMin[0], this.sweepMin[1], this.sweepMax[0], this.sweepMax[1], this.testBrush);
    return this.found;
  }

  private overlapOut: number[] = [];
  private overlapCount = 0;
  private overlapStamp = new Uint32Array(0);
  private overlapVisit = 0;
  private readonly overlapBrush = (b: number): void => {
    // Touching counts: use non-strict bounds rejection.
    if (this.rejects(b, false)) return;
    const solid = this.brushes[b * 4 + 3];
    if (this.overlapStamp[solid] === this.overlapVisit) return;
    this.toLocal(solid);
    if (!this.boxInBrush(b)) return;
    this.overlapStamp[solid] = this.overlapVisit;
    this.overlapOut[this.overlapCount++] = solid;
  };

  /**
   * Solids whose brushes overlap or touch the box at `origin`, in brush order: written to the start
   * of `out`, returning how many. `out` is never shortened (that frees its storage, and refilling it
   * every tick made garbage). Pass mask -1 to ignore brush contents (trigger volumes).
   */
  overlapping(
    origin: Readonly<Vec3>,
    mins: Readonly<Vec3>,
    maxs: Readonly<Vec3>,
    mask: number,
    out: number[],
  ): number {
    if (this.overlapStamp.length !== this.data.solids.length)
      this.overlapStamp = new Uint32Array(this.data.solids.length);
    if (++this.overlapVisit === 0xffffffff) {
      this.overlapStamp.fill(0);
      this.overlapVisit = 1;
    }
    this.overlapOut = out;
    this.overlapCount = 0;
    this.setupBox(origin, mins, maxs, mask);
    this.query(this.sweepMin[0], this.sweepMin[1], this.sweepMax[0], this.sweepMax[1], this.overlapBrush);
    return this.overlapCount;
  }
}
